import React, {
  ComponentType,
  FC,
  Fragment,
  SyntheticEvent,
  isValidElement,
  useCallback,
  useEffect,
} from 'react';

import { Portal } from '@radix-ui/react-portal';
import { ESCAPE_KEY } from 'constants/keyCodes';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { ArrowBack, SvgIconProps } from 'ui/icons';
import { Spinner } from 'ui/spinner';
import { spacing } from 'ui/theme';
import { useNavBarHeight } from 'utils/hooks/useNavBarHeight';
import noop from 'utils/noop';
import { DrawerBody as TakeoverBody, DrawerFooter as TakeoverFooter } from '../drawer';
import {
  ArrowContainer,
  HeaderBarTitle,
  HeaderBarWrapper,
  HeaderContentWrapper,
  TakeoverDrawer,
  TakeoverWrapper,
  BadgeWrapper,
} from './Takeover.styles';

export const TakeoverTestIds = {
  component: 'takeover-component',
  main: 'takeover-main',
  spinner: 'takeover-spinner',
  backButton: 'takeover-back-button',
};

export { TakeoverBody, TakeoverFooter };

export interface TakeoverProps {
  Badge?: React.ReactNode;
  /** Whether content inside takeover is loading */
  isLoading?: boolean;
  /** Title of the Takeover.  Will be placed in the header. */
  title?: string;
  /** Header Bar inner content */
  HeaderBarContent?: React.ReactNode;
  /** Takeover body */
  Body?: React.ReactNode;
  /** Takeover footer */
  Footer?: React.ReactNode;
  alignFooterRight?: boolean;
  /** Callback when the takeover is closed */
  onClose?: (e: SyntheticEvent) => void;
  /** Whether content inside takeover is full width and height */
  noPadding?: boolean;
  /** Whether takeover should have shaded overlay */
  isOverlay?: boolean;
  className?: string;
  isMainApp?: boolean;
  /** One of the pre-defined sizes for the Drawer component, or a string compatible with css width, or a number (of pixels) */
  size?: string | number;
  /** Whether the takeover should be rendered in a portal */
  withPortal?: boolean;
  CloseIcon?: ComponentType<SvgIconProps>;
}

export const Takeover: FC<React.PropsWithChildren<TakeoverProps>> = ({
  Badge,
  isLoading = false,
  title,
  onClose = noop,
  HeaderBarContent,
  Body,
  Footer,
  alignFooterRight = false,
  children,
  noPadding = false,
  isOverlay = false,
  withPortal = false,
  isMainApp,
  className = '',
  CloseIcon = ArrowBack,
  size = `calc(100% - ${spacing.x4})`,
  ...props
}): JSX.Element => {
  const navBarHeight = useNavBarHeight();
  const handleKeyDown = useCallback(
    (e: any) => {
      const { keyCode } = e;

      // escape key pressed
      if (keyCode === ESCAPE_KEY) {
        onClose(e);
      }
    },
    [onClose],
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const PortalWrapper = withPortal ? Portal : Fragment;

  return (
    <PortalWrapper {...(withPortal && { asChild: true })}>
      <TakeoverWrapper
        data-test={TakeoverTestIds.component}
        id={TakeoverTestIds.component}
        $isOverlay={isOverlay}
        $isMainApp={isMainApp}
        className={className}
        {...props}
      >
        <HeaderBarWrapper $navBarHeight={navBarHeight}>
          <ArrowContainer>
            <IconButton
              variant={BUTTON_VARIANTS.GHOSTINVERTED}
              onClick={onClose}
              tabIndex={-1}
              data-test={TakeoverTestIds.backButton}
            >
              <CloseIcon isColorInherited={true} />
            </IconButton>
          </ArrowContainer>
          <HeaderContentWrapper>
            {title && <HeaderBarTitle data-test="header-bar-title">{title}</HeaderBarTitle>}
            {isValidElement(Badge) && <BadgeWrapper>{Badge}</BadgeWrapper>}
            {isValidElement(HeaderBarContent) && HeaderBarContent}
          </HeaderContentWrapper>
        </HeaderBarWrapper>
        <TakeoverDrawer data-test={TakeoverTestIds.main} size={size}>
          {isLoading ? (
            <Spinner data-test={TakeoverTestIds.spinner} />
          ) : (
            <>
              {children}
              {isValidElement(Body) && <TakeoverBody noPadding={noPadding}>{Body}</TakeoverBody>}
              {isValidElement(Footer) && (
                <TakeoverFooter alignRight={alignFooterRight}>{Footer}</TakeoverFooter>
              )}
            </>
          )}
        </TakeoverDrawer>
      </TakeoverWrapper>
    </PortalWrapper>
  );
};
